import { useState } from 'react';
import { Upload, Button, Space, Tag, Image, Tooltip, theme, useApp } from '../ui';
import { Upload as UploadIcon, Trash2, Link, FileText, Video, Image as ImageIcon, File } from 'lucide-react';
import type { UploadProps } from '../ui';
import { api } from '../api/client';

export type UploadCategory =
  | 'product-image' | 'product-video' | 'product-brochure'
  | 'retailer-photo' | 'farmer-photo' | 'profile-photo'
  | 'receipt' | 'evidence' | 'pod' | 'attachment' | 'invoice' | 'event-photo' | 'banner' | 'payment-qr';

const ACCEPT: Record<UploadCategory, string> = {
  'product-image':    'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
  'product-video':    'video/mp4,video/quicktime,video/webm',
  'product-brochure': 'application/pdf',
  'retailer-photo':   'image/*',
  'farmer-photo':     'image/*',
  'profile-photo':    'image/*',
  'receipt':          'image/*,application/pdf',
  'evidence':         'image/*,application/pdf,video/*',
  'pod':              'image/*,application/pdf',
  'attachment':       '*/*',
  'invoice':          'application/pdf',
  'event-photo':      'image/*',
  'banner':           'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
  'payment-qr':       'image/png,image/jpeg,image/webp',
};

const MAX_MB: Record<UploadCategory, number> = {
  'product-image': 5, 'product-video': 50, 'product-brochure': 20,
  'retailer-photo': 5, 'farmer-photo': 5, 'profile-photo': 5,
  'receipt': 10, 'evidence': 25, 'pod': 10, 'attachment': 25, 'invoice': 20, 'event-photo': 5,
  'banner': 8, 'payment-qr': 2,
};

interface Props {
  /** Current URL — comes from / goes to the surrounding Form.Item via value/onChange */
  value?: string;
  onChange?: (url: string) => void;
  category: UploadCategory;
  /** Optional label hint shown on the button */
  buttonLabel?: string;
  /** Show inline thumbnail (only meaningful for images) */
  preview?: boolean;
  /** Allow pasting / typing a URL too (default true) */
  allowUrlPaste?: boolean;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
}

function fileIcon(category: UploadCategory, url?: string): JSX.Element {
  if (!url) return <File size={16} />;
  const lower = url.toLowerCase();
  if (category.includes('image') || /\.(png|jpe?g|webp|gif|svg)(\?|$)/.test(lower)) return <ImageIcon size={16} />;
  if (category.includes('video') || /\.(mp4|mov|webm)(\?|$)/.test(lower)) return <Video size={16} />;
  if (category.includes('brochure') || /\.pdf(\?|$)/.test(lower)) return <FileText size={16} />;
  return <File size={16} />;
}

export function FileUploadField({
  value, onChange, category, buttonLabel, preview = true, allowUrlPaste = true, size = 'middle', disabled,
}: Props) {
  const { token } = theme.useToken();
  const { message } = useApp();
  const [busy, setBusy] = useState(false);
  const btnSize = size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'md';

  const uploadProps: UploadProps = {
    accept: ACCEPT[category],
    showUploadList: false,
    multiple: false,
    disabled: disabled || busy,
    beforeUpload: async (file) => {
      const maxBytes = MAX_MB[category] * 1024 * 1024;
      if (file.size > maxBytes) {
        message.error(`File too large — max ${MAX_MB[category]} MB`);
        return Upload.LIST_IGNORE;
      }
      setBusy(true);
      const form = new FormData();
      form.append('file', file);
      try {
        const r = await api.post(`/uploads/${category}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = r.data?.data ?? r.data;
        const url = data?.url;
        if (!url) throw new Error('Server did not return a URL');
        onChange?.(url);
        message.success(`Uploaded ${file.name}`);
      } catch (e: any) {
        message.error(e.response?.data?.error?.message ?? e.message ?? 'Upload failed');
      } finally {
        setBusy(false);
      }
      return Upload.LIST_IGNORE;
    },
  };

  const showThumb = preview && value && /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(value);

  return (
    <Space direction="vertical" size={6} style={{ width: '100%' }}>
      <Space wrap>
        <Upload {...uploadProps}>
          <Button size={btnSize} icon={<UploadIcon size={16} />} loading={busy} disabled={disabled}>
            {buttonLabel ?? (value ? 'Replace file' : `Upload ${category.split('-').join(' ')}`)}
          </Button>
        </Upload>
        {value && (
          <>
            <Tooltip title="Open in a new tab">
              <Button size={btnSize} icon={<Link size={16} />} href={value} target="_blank" rel="noopener">
                Open
              </Button>
            </Tooltip>
            <Tooltip title="Remove">
              <Button size={btnSize} variant="danger" aria-label="Remove" icon={<Trash2 size={16} />} onClick={() => onChange?.('')} disabled={disabled} />
            </Tooltip>
          </>
        )}
      </Space>

      {value && allowUrlPaste && (
        <Tag icon={fileIcon(category, value)} style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value.length > 70 ? `…${value.slice(-65)}` : value}
        </Tag>
      )}

      {showThumb && (
        <Image
          src={value}
          width={80}
          height={80}
          style={{ objectFit: 'cover', borderRadius: 6, border: `1px solid ${token.colorBorderSecondary}` }}
          preview={{ src: value }}
        />
      )}
    </Space>
  );
}
